import { CSSProperties, SelectHTMLAttributes } from 'react';
import { motion } from 'framer-motion';

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'style'> {
  label: string;
  error?: string;
  options: { value: string; label: string }[];
}

export default function Select({ label, error, options, required, ...props }: SelectProps) {
  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-xs)',
    width: '100%'
  };

  const labelStyles: CSSProperties = {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--color-text)',
    display: 'flex',
    gap: '0.25rem'
  };

  const selectStyles: CSSProperties = {
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: `2px solid ${error ? 'var(--color-error)' : 'var(--color-border)'}`,
    borderRadius: 'var(--radius-md)',
    outline: 'none',
    transition: 'all 0.2s',
    backgroundColor: 'white',
    cursor: 'pointer'
  };

  const errorStyles: CSSProperties = {
    fontSize: '0.75rem',
    color: 'var(--color-error)',
    margin: 0
  };

  return (
    <motion.div
      style={containerStyles}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
    >
      <label style={labelStyles}>
        {label}
        {required && <span style={{ color: 'var(--color-error)' }}>*</span>}
      </label>
      <select
        {...props}
        required={required}
        style={selectStyles}
        onFocus={(e) => {
          e.target.style.borderColor = 'var(--color-primary)';
          e.target.style.boxShadow = '0 0 0 3px rgba(44, 191, 204, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = error ? 'var(--color-error)' : 'var(--color-border)';
          e.target.style.boxShadow = 'none';
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <p style={errorStyles}>{error}</p>}
    </motion.div>
  );
}
